import React, { useState, useEffect, useRef } from 'react';
import Wordmark from '../brand/Wordmark';
import {
  Sun,
  Activity,
  DollarSign,
  Users,
  Settings,
  Pin,
  PinOff,
  Menu,
  X,
  LogOut,
  Shield,
  BookOpen,
  Sparkles,
  Crown,
  Lock
} from 'lucide-react';
import SidebarSettings from './SidebarSettings';
import { useTheme } from '../../context/ThemeContext';
import { ROUTES } from '../../constants/routes';

const CollapsibleSidebar = ({ currentUser, activeTab, setActiveTab, onLogout, onShowLegal, onShowPricing, onProfileUpdate, showSettings: externalShowSettings, setShowSettings: externalSetShowSettings }) => {
  useTheme();

  const [isPhone, setIsPhone] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsPhone(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isPinned, setIsPinned] = useState(() => {
    return localStorage.getItem('sidebarPinned') === 'true';
  });
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(() => {
    return !localStorage.getItem('sidebarTooltipDismissed');
  });
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showSettingsInternal, setShowSettingsInternal] = useState(false);
  const showSettings = externalShowSettings !== undefined ? externalShowSettings : showSettingsInternal;
  const setShowSettings = externalSetShowSettings || setShowSettingsInternal;

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin' || currentUser?.username?.toLowerCase() === 'shreyaspillai17';
  // effective_plan (set by the backend) already accounts for admin bypass
  // and the phase-1 "gating not live yet" state — falls back to `plan` for
  // any cached currentUser fetched before this field existed. Used only to
  // decide what's locked in the nav — nothing is locked during phase 1.
  const planForGating = currentUser?.effective_plan ?? currentUser?.plan;
  const isTeamPlan = planForGating === 'team';
  // True billing plan (ignores the phase-1/admin bypass) — drives the
  // "Upgrade" pill, which should show for real free-plan users regardless
  // of whether gating is actively locking anything yet.
  const isBillingFree = !currentUser?.plan || currentUser.plan === 'free';

  const navItems = [
    { id: ROUTES.TODAY,   label: 'Today', icon: Sun,        tooltip: 'Today',                     accent: 'rgba(45,212,191,0.12)' },
    { id: ROUTES.BUILD,   label: 'Build', icon: Activity,   tooltip: 'Build Tracker',             accent: 'rgba(99,102,241,0.12)' },
    // AI is metered, not locked — free users get 5 generations a day, so the
    // tab must open for them. The upgrade prompt lives at the limit, not here.
    { id: ROUTES.AI,      label: 'AI',    icon: Sparkles,   tooltip: 'AI Co-founder',             accent: 'rgba(168,85,247,0.12)' },
    { id: ROUTES.COMPANY, label: 'Money', icon: DollarSign, tooltip: 'Money — Finance & Runway',  accent: 'rgba(251,191,36,0.12)' },
    { id: ROUTES.TEAM,    label: 'Team',  icon: Users,      tooltip: 'Team Hub',                  accent: 'rgba(52,211,153,0.12)', locked: !isTeamPlan },
    ...(isAdmin ? [{ id: ROUTES.ADMIN, label: 'Admin', icon: Shield, tooltip: 'Admin Dashboard', accent: 'rgba(248,113,113,0.12)' }] : [])
  ];

  useEffect(() => {
    const savedExpanded = localStorage.getItem('sidebarExpanded');
    if (savedExpanded === 'true' && isPinned) {
      setIsExpanded(true);
    }
  }, [isPinned]);

  useEffect(() => {
    // Sidebar is now locked at 60px on desktop (preview-style). No keyboard shortcut.
  }, []);

  const expandTimeoutRef = useRef(null);
  const collapseTimeoutRef = useRef(null);

  const handleMouseEnter = () => {
    // Desktop sidebar is locked at 60px to match preview — no hover expand.
  };

  const handleMouseLeave = () => {
    // Desktop sidebar is locked at 60px to match preview — no hover collapse.
  };

  useEffect(() => {
    return () => {
      if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);
      if (collapseTimeoutRef.current) clearTimeout(collapseTimeoutRef.current);
    };
  }, []);

  const togglePin = () => {
    const newPinned = !isPinned;
    setIsPinned(newPinned);
    localStorage.setItem('sidebarPinned', newPinned);
    if (newPinned) {
      setIsExpanded(true);
      localStorage.setItem('sidebarExpanded', 'true');
    }
    dismissTooltip();
  };

  const toggleMobile = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const handleNavClick = (tabId, e) => {
    const item = navItems.find(n => n.id === tabId);
    if (item?.locked) {
      onShowPricing?.();
      if (isMobileOpen) setIsMobileOpen(false);
      return;
    }
    setActiveTab(tabId);
    if (isMobileOpen) {
      setIsMobileOpen(false);
    }
  };

  const dismissTooltip = () => {
    setShowTooltip(false);
    localStorage.setItem('sidebarTooltipDismissed', 'true');
  };

  const getUserInitials = () => {
    const name = currentUser?.full_name || currentUser?.username || '';
    if (name) return name.slice(0, 2).toUpperCase();
    return 'U';
  };

  const sidebarExpanded = isExpanded || isMobileOpen;

  // Shared transition for expand/collapse text
  const textExpandClass = sidebarExpanded
    ? 'opacity-100 translate-x-0 max-w-[140px] flex-1'
    : 'opacity-0 -translate-x-2 max-w-0 w-0 overflow-hidden pointer-events-none hidden';

  return (
    <>
      {/* Mobile Hamburger Button */}
      {!isPhone && (
      <button
        onClick={toggleMobile}
        className="fixed bottom-20 left-4 z-50 lg:hidden"
        style={{
          padding: '10px',
          borderRadius: '14px',
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid rgba(255,255,255,0.07)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.04)',
          transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
          cursor: 'pointer',
        }}
        aria-label="Toggle menu"
      >
        <div className="relative" style={{ width: '18px', height: '18px' }}>
          <Menu className={`absolute inset-0 transition-all duration-400 ease-[cubic-bezier(0.32,0.72,0,1)] ${
            isMobileOpen ? 'opacity-0 rotate-90 scale-50' : 'opacity-100 rotate-0 scale-100'
          }`} style={{ width: '18px', height: '18px', color: 'var(--text-primary)', strokeWidth: 1.5 }} />
          <X className={`absolute inset-0 transition-all duration-400 ease-[cubic-bezier(0.32,0.72,0,1)] ${
            isMobileOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-50'
          }`} style={{ width: '18px', height: '18px', color: 'var(--text-primary)', strokeWidth: 1.5 }} />
        </div>
      </button>
      )}

      {/* Mobile Backdrop */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ease-out ${
          isMobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
        onClick={toggleMobile}
      />

      {/* Sidebar — locked at 60px on desktop to match preview */}
      <aside
        className={`
          fixed left-0 top-0 h-screen z-50
          transition-transform duration-400 ease-[cubic-bezier(0.32,0.72,0,1)]
          ${sidebarExpanded ? 'w-[220px]' : 'w-[60px]'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{
          background: 'linear-gradient(180deg, var(--bg-surface) 0%, var(--bg-base) 100%)',
          borderRight: '1px solid rgba(255,255,255,0.06)',
          boxShadow: sidebarExpanded
            ? '4px 0 24px rgba(0,0,0,0.25), 1px 0 0 rgba(255,255,255,0.03)'
            : '1px 0 0 rgba(255,255,255,0.03)',
        }}
      >
        <div className="flex flex-col h-full">

          {/* ── Brand Header ── */}
          <div className={`flex items-center h-[56px] flex-shrink-0 ${sidebarExpanded ? 'justify-between px-3' : 'justify-center px-0'}`}
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className={`flex items-center min-w-0 overflow-hidden ${sidebarExpanded ? 'gap-2.5' : 'justify-center w-full'}`}>
              {/* Brand — the wordmark shows in both states; it inherits --text-primary
                  and --accent-primary, so it flips with the theme on its own. */}
              <div className="flex flex-col min-w-0 overflow-hidden" style={{ gap: '2px' }}>
                <Wordmark
                  plusColor="var(--accent-primary)"
                  style={{
                    ...(sidebarExpanded
                      ? { height: '9.6px', width: 'auto' }
                      : { width: '42px', height: 'auto' }),
                    display: 'block',
                    color: 'var(--text-primary)',
                    transition: 'width 400ms cubic-bezier(0.25,0.1,0.25,1), height 400ms cubic-bezier(0.25,0.1,0.25,1)',
                  }}
                />
                <span className={`text-[9px] font-semibold tracking-[0.08em] uppercase whitespace-nowrap transition-all duration-400 ease-[cubic-bezier(0.25,0.1,0.25,1)] overflow-hidden ${textExpandClass}`}
                  style={{ color: 'var(--text-tertiary)', transitionDelay: sidebarExpanded ? '50ms' : '0ms' }}>
                  Founder OS
                </span>
              </div>
            </div>

            {/* Close (mobile) / Pin (desktop) */}
            {isMobileOpen && (
              <button onClick={toggleMobile} className="flex-shrink-0 lg:hidden p-1.5 rounded-lg transition-all duration-200"
                style={{ color: 'var(--text-muted)' }}
                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                aria-label="Close sidebar">
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
            {sidebarExpanded && window.innerWidth > 1024 && false && (
              <button onClick={togglePin}
                className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-200"
                style={{
                  color: isPinned ? 'var(--accent-primary)' : 'var(--text-muted)',
                  backgroundColor: isPinned ? 'rgba(45,212,191,0.1)' : 'transparent',
                  border: isPinned ? '1px solid rgba(45,212,191,0.15)' : '1px solid transparent',
                }}
                onMouseOver={(e) => { if (!isPinned) { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}}
                onMouseOut={(e) => { if (!isPinned) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}}
                title={isPinned ? 'Unpin sidebar (⌘B)' : 'Pin sidebar (⌘B)'}>
                {isPinned ? <Pin className="w-3.5 h-3.5" strokeWidth={2} /> : <PinOff className="w-3.5 h-3.5" strokeWidth={1.5} />}
              </button>
            )}
          </div>

          {/* ── Navigation ── */}
          <nav className={`flex-1 overflow-y-auto overflow-x-hidden pt-4 pb-3 ${sidebarExpanded ? 'px-2.5' : 'px-0'}`}>
            <div className="flex flex-col gap-1 items-center">

              {navItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;

                return (
                  <div key={item.id} className="relative w-full">
                    {/* Active indicator bar */}
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full transition-all duration-300"
                      style={{
                        height: isActive ? '20px' : '0px',
                        backgroundColor: isActive ? 'var(--accent-primary)' : 'transparent',
                        opacity: isActive ? 1 : 0,
                        boxShadow: isActive ? '0 0 8px rgba(45,212,191,0.4)' : 'none',
                      }}
                    />

                    <button
                      onClick={(e) => handleNavClick(item.id, e)}
                      onMouseEnter={() => setHoveredItem(item.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`w-full flex rounded-xl transition-all duration-200 ${
                        sidebarExpanded
                          ? 'flex-row items-center gap-3 px-2.5 py-2'
                          : 'flex-col items-center justify-center gap-0.5 px-0 py-1.5'
                      }`}
                      style={{
                        backgroundColor: isActive
                          ? (sidebarExpanded ? item.accent : 'rgba(45,212,191,0.08)')
                          : 'transparent',
                        color: isActive ? 'var(--text-primary)' : 'var(--text-tertiary)',
                        border: isActive
                          ? (sidebarExpanded ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(45,212,191,0.15)')
                          : '1px solid transparent',
                        ...(isMobileOpen ? { animation: `sidebarNavStagger 0.45s cubic-bezier(0.32,0.72,0,1) ${index * 55 + 100}ms both` } : {}),
                      }}
                      onMouseOver={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)';
                          e.currentTarget.style.color = 'var(--text-secondary)';
                          e.currentTarget.style.border = '1px solid rgba(255,255,255,0.04)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = 'var(--text-tertiary)';
                          e.currentTarget.style.border = '1px solid transparent';
                        }
                      }}
                      aria-label={item.label}
                    >
                      {/* Icon — filled container when expanded, bare teal stroke when collapsed (matches preview) */}
                      {sidebarExpanded ? (
                        <div className="relative w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200"
                          style={isActive
                            ? { backgroundColor: 'var(--accent-primary)', color: 'white', boxShadow: '0 2px 8px rgba(45,212,191,0.3)' }
                            : { backgroundColor: 'transparent', color: 'inherit' }
                          }>
                          <Icon className="w-[17px] h-[17px]" strokeWidth={isActive ? 2.2 : 1.5} />
                          {item.locked && (
                            <Lock className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5" style={{ color: '#f59e0b', strokeWidth: 2.5 }} />
                          )}
                        </div>
                      ) : (
                        <div className="relative">
                          <Icon
                            style={{
                              width: '18px', height: '18px', flexShrink: 0,
                              stroke: isActive ? 'var(--accent-primary)' : 'var(--text-muted)',
                              strokeWidth: 1.75,
                              fill: 'none',
                            }}
                          />
                          {item.locked && (
                            <Lock className="absolute -bottom-0.5 -right-0.5 w-2 h-2" style={{ color: '#f59e0b', strokeWidth: 2.5 }} />
                          )}
                        </div>
                      )}

                      {/* Label — full text when expanded, tiny 9px label when collapsed */}
                      {sidebarExpanded ? (
                        <span className={`whitespace-nowrap text-left overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${textExpandClass}`}
                          style={{
                            fontSize: '13px',
                            fontWeight: isActive ? 600 : 500,
                            letterSpacing: '0.01em',
                            transitionDelay: `${index * 30}ms`,
                          }}>
                          {item.label}
                        </span>
                      ) : (
                        <span style={{
                          fontSize: '9px',
                          fontWeight: 500,
                          color: isActive ? 'var(--accent-primary)' : 'var(--text-muted)',
                          letterSpacing: '0.02em',
                          lineHeight: 1,
                          userSelect: 'none',
                        }}>
                          {item.label}
                        </span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </nav>

          {/* ── Bottom Section ── */}
          <div className={`flex flex-col gap-1 items-center ${sidebarExpanded ? 'px-2.5' : 'px-0'} pb-3 pt-2`} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>

            {/* User card */}
            <div className={`w-full flex rounded-xl overflow-hidden transition-all duration-200 cursor-pointer ${sidebarExpanded ? 'flex-row items-center gap-3 px-2.5 py-2' : 'flex-col items-center justify-center gap-0.5 px-0 py-1.5'}`}
              style={{
                color: 'var(--text-tertiary)',
                border: '1px solid transparent',
                ...(isMobileOpen ? { animation: 'sidebarNavStagger 0.45s cubic-bezier(0.32, 0.72, 0, 1) 350ms both' } : {}),
              }}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
            >
              {/* Avatar */}
              <div className={`flex items-center justify-center flex-shrink-0 font-bold ${sidebarExpanded ? 'w-8 h-8 rounded-lg text-[10px]' : 'w-7 h-7 rounded-full text-[9px]'}`}
                style={{
                  background: 'linear-gradient(135deg, rgba(45,212,191,0.2) 0%, rgba(129,140,248,0.2) 100%)',
                  color: 'var(--accent-primary)',
                  border: '1px solid rgba(45,212,191,0.15)',
                  boxShadow: '0 0 12px rgba(45,212,191,0.06)',
                  letterSpacing: '0.02em',
                }}>
                {getUserInitials()}
              </div>
              {sidebarExpanded ? (
                <span className={`whitespace-nowrap text-left overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${textExpandClass}`}
                  style={{ fontSize: '13px', fontWeight: 500, letterSpacing: '0.01em' }}>
                  {currentUser?.full_name || currentUser?.username || 'User'}
                </span>
              ) : (
                <span style={{ fontSize: '9px', fontWeight: 500, color: 'var(--text-muted)', letterSpacing: '0.02em', lineHeight: 1, userSelect: 'none' }}>
                  {getUserInitials()}
                </span>
              )}
            </div>

            {/* Upgrade pill — only shown to free-plan users; opens the Pricing overlay */}
            {isBillingFree && (
              <div className="relative w-full">
                <button
                  onClick={() => { onShowPricing?.(); if (isMobileOpen) setIsMobileOpen(false); }}
                  className={`w-full flex rounded-xl transition-all duration-200 ${
                    sidebarExpanded ? 'flex-row items-center gap-3 px-2.5 py-2' : 'flex-col items-center justify-center gap-0.5 px-0 py-1.5'
                  }`}
                  style={{
                    color: '#f59e0b',
                    backgroundColor: 'rgba(251,191,36,0.08)',
                    border: '1px solid rgba(251,191,36,0.18)',
                    cursor: 'pointer',
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(251,191,36,0.14)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'rgba(251,191,36,0.08)'; }}
                  aria-label="Upgrade plan"
                >
                  {sidebarExpanded ? (
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Crown className="w-[16px] h-[16px]" strokeWidth={1.5} />
                    </div>
                  ) : (
                    <Crown style={{ width: '16px', height: '16px', flexShrink: 0, strokeWidth: 1.5, fill: 'none' }} />
                  )}
                  {sidebarExpanded ? (
                    <span className={`whitespace-nowrap text-left overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${textExpandClass}`}
                      style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.01em' }}>
                      Upgrade
                    </span>
                  ) : (
                    <span style={{ fontSize: '9px', fontWeight: 600, color: '#f59e0b', letterSpacing: '0.02em', lineHeight: 1, userSelect: 'none' }}>
                      Upgrade
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Utility buttons */}
            {[
              { id: 'getstarted', label: 'Get Started', icon: BookOpen, onClick: () => { onShowLegal?.('getstarted'); if (isMobileOpen) setIsMobileOpen(false); }, delay: 400 },
              { id: 'settings', label: 'Settings', icon: Settings, onClick: () => { setShowSettings(true); if (isMobileOpen) setIsMobileOpen(false); }, delay: 450 },
            ].map((btn) => (
              <div key={btn.id} className="relative w-full">
                <button
                  onClick={btn.onClick}
                  onMouseEnter={() => setHoveredItem(btn.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                  className={`w-full flex rounded-xl transition-all duration-200 ${
                    sidebarExpanded ? 'flex-row items-center gap-3 px-2.5 py-2' : 'flex-col items-center justify-center gap-0.5 px-0 py-1.5'
                  }`}
                  style={{
                    color: 'var(--text-muted)',
                    backgroundColor: 'transparent',
                    border: '1px solid transparent',
                    cursor: 'pointer',
                    ...(isMobileOpen ? { animation: `sidebarNavStagger 0.45s cubic-bezier(0.32,0.72,0,1) ${btn.delay}ms both` } : {}),
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                  aria-label={btn.label}
                >
                  {sidebarExpanded ? (
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0">
                      <btn.icon className="w-[16px] h-[16px]" strokeWidth={1.5} />
                    </div>
                  ) : (
                    <btn.icon style={{ width: '16px', height: '16px', flexShrink: 0, strokeWidth: 1.5, fill: 'none' }} />
                  )}
                  {sidebarExpanded ? (
                    <span className={`whitespace-nowrap text-left overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${textExpandClass}`}
                      style={{ fontSize: '13px', fontWeight: 500, letterSpacing: '0.01em' }}>
                      {btn.label}
                    </span>
                  ) : (
                    <span style={{ fontSize: '9px', fontWeight: 500, color: 'var(--text-muted)', letterSpacing: '0.02em', lineHeight: 1, userSelect: 'none' }}>
                      {btn.label === 'Get Started' ? 'Guide' : btn.label}
                    </span>
                  )}
                </button>
                {false && !sidebarExpanded && hoveredItem === btn.id && window.innerWidth > 768 && (
                  <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 whitespace-nowrap z-50 pointer-events-none"
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      borderRadius: '10px',
                      backgroundColor: 'var(--bg-elevated)',
                      color: 'var(--text-primary)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.03)',
                    }}>
                    {btn.label}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </aside>

      {/* Settings Modal */}
      <SidebarSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        currentUser={currentUser}
        onLogout={onLogout}
        onShowLegal={onShowLegal}
        onShowPricing={onShowPricing}
        onProfileUpdate={onProfileUpdate}
      />
    </>
  );
};

export default CollapsibleSidebar;
